<template>
    <div>
        <Dialog v-bind:value="value" @input="$emit('input', $event)"
                :title="position ? 'Position bearbeiten' : 'Neue Position hinzufügen'"
                @ok="submit" :loading.sync="loading" :width="!isDefaultZone || position ? '650px' : '500px'"
                :ok-disabled="readOnly"
                @cancel="cancel">
            <form>
                <v-row>
                    <v-col>
                        <Dropdown v-model="name" :items="positions"
                                  label="Bezeichnung"
                                  item-text="Name" item-value="Name"
                                  :error-messages="name_errors"
                                  @input="$v.name.$touch()"
                                  @blur="$v.name.$touch()"
                        />
                    </v-col>
                    <v-col>
                        <Dropdown v-model="variant" :items="variants"
                                  label="Variante"
                                  item-text="Id" item-value="Id"
                                  :error-messages="variant_errors"
                                  @input="$v.variant.$touch()"
                                  @blur="$v.variant.$touch()"
                        />
                    </v-col>
                </v-row>
                <v-row class="mt-0">
                    <v-col>
                        <Input
                            v-model="posVar" type="number"
                            :label="position ? 'Pos-Variable #1' : 'Pos-Variable'"
                            :error-messages="posVar_errors"
                            @input="$v.posVar.$touch()"
                            @blur="$v.posVar.$touch()"></Input></v-col>
                    <v-col>
                        <Input v-if="position"
                               v-model="position2" type="number"
                               label="Pos-Variable #2"
                               placeholder="-1, wenn Inhalt gelöscht werden soll"></Input></v-col>
                </v-row>
                <div class="d-flex align-center">
                    <Dropdown
                        v-model="sizes"
                        :items="position_sizes"
                        item-text="Name" item-value="Id"
                        label="Sizes"
                        :multiple="true"
                        @delete="removeSize"
                        hide-details="auto"
                    />
                    <TooltipIcon v-if="!readOnly" tooltip="Mehrere Sizes gleichzeitig hinzufügen"
                                 @click="showAddMultipleSizesDialog = true">mdi-plus-box-multiple
                    </TooltipIcon>
                    <Dialog v-model="showAddMultipleSizesDialog" title="Mehrere Sizes gleichzeitig hinzufügen"
                            @ok="addMultipleSizes" :loading.sync="loading" :width="'500px'"
                            :ok-disabled="readOnly"
                            @cancel="showAddMultipleSizesDialog = false;multipleSizes = null;">
                        <Input v-model="multipleSizes" label="Sizes" @enter="addMultipleSizes"
                               hint="Mit einem Semikolon separiert eintragen z.B. 320x50; 320x100; 320x150"></Input>
                    </Dialog>
                </div>
                <Toggle v-for="(toggle, i) in formSwitches" :key="'switch_'+i"
                        :label="toggle.label"
                        v-model="$data[toggle.vModel]" hide-details
                />
                <Textarea class="mt-6" label="Notizen" v-model.trim="note" :disabled="readOnly" rows="3"
                          outlined></Textarea>
            </form>
            <template v-slot:button v-if="position && !readOnly">
                <Button v-if="!readOnly && isDefaultZone" :text="true" :color="ada.pink" @click="submitAndOverwrite"
                        :loading="overwriteLoading"
                        title="Speichern und für alle Parents übernehmen">
                    Speichern & übernehmen
                </Button>
                <v-tooltip top v-bind="$attrs" v-if="!isDefaultZone && !position.Override">
                    <template v-slot:activator="{ on, attrs }">
                        <Button :text="true" :color="ada.blue" v-if="!readOnly && (isDefaultZone || allowDelete)"
                                @click="showResetPositionDialog = true"
                                v-bind="attrs"
                                v-on="on">
                            Zurücksetzen
                        </Button>
                    </template>
                    <span>Auf den _default Zonen Stand zurücksetzen</span>
                </v-tooltip>
                <Button :text="true" :color="ada.red" v-if="!readOnly && (isDefaultZone || allowDelete)"
                        @click="showDeletePositionDialog = true">
                    Löschen
                </Button>
            </template>
        </Dialog>
        <OverwritePositionsDialog v-model="showOverwriteDialog" :position="position"></OverwritePositionsDialog>
        <DeletePositionDialog v-model="showDeletePositionDialog" :position="position"></DeletePositionDialog>
        <ResetPositionDialog v-model="showResetPositionDialog" :position="position"
                             @reset="$emit('input', false)"></ResetPositionDialog>
        <Snackbar v-model="successSnackbar" color="success">Position erfolgreich
            {{ position ? 'gespeichert.' : 'hinzugefügt.' }}
        </Snackbar>
    </div>
</template>

<script>
import Dialog from "../../../components/Dialog";
import Utils from "../../../scripts/Utils";
import {validationMixin} from "vuelidate";
import {required} from "vuelidate/lib/validators";
import Dropdown from "../../../components/Inputs/Dropdown";
import Toggle from "../../../components/buttons/Toggle";
import Input from "../../../components/Inputs/Input";
import Snackbar from "../../../components/Snackbar";
import ada from "../../../scss/ada_colors.scss";
import Button from "../../../components/buttons/Button";
import OverwritePositionsDialog from "./OverwritePositionsDialog";
import DeletePositionDialog from "./DeletePositionDialog";
import Textarea from "../../../components/Inputs/Textarea";
import ResetPositionDialog from "./ResetPositionDialog";
import TooltipIcon from "../../../components/TooltipIcon";

export default {
    name: "PositionsDialog",
    components: {
        TooltipIcon,
        ResetPositionDialog,
        Textarea,
        DeletePositionDialog, OverwritePositionsDialog, Button, Snackbar, Input, Toggle, Dropdown, Dialog
    },
    props: {
        value: {},
        position: {},
        readOnly: {},
    },
    mixins: [validationMixin],
    validations: {
        name: {required},
        variant: {required},
        posVar: {required}
    },
    data: () => {
        return {
            loading: false,
            name: null,
            variant: null,
            slotStatus: null,
            lazyLoad: null,
            splitQueue: null,
            adLabel: null,
            posVar: 1,
            sizes: null,
            position2: '',
            note: null,
            successSnackbar: false,
            overwrite: false,
            overwriteLoading: false,
            showOverwriteDialog: false,
            showDeletePositionDialog: false,
            showResetPositionDialog: false,
            showAddMultipleSizesDialog: false,
            currentCancelToken: null,
            multipleSizes: null,
            ada
        }
    },
    mounted() {
        this.fetchPositions();
        this.init();
    },
    computed: {
        vuexState() {
            return this.$store.state.zones;
        },
        selectedSite() {
            return this.$store.state.display.selectedSite;
        },
        selectedType() {
            return this.$store.state.display.selectedType;
        },
        isDefaultZone() {
            return this.vuexState.zoneDetails ? this.vuexState.zoneDetails.Zone.Name === '_default' : false;
        },
        allowDelete() {
            return this.vuexState.zoneDetails ? this.vuexState.zoneDetails.Zone.Oop === 0 && this.vuexState.zoneDetails.Zone.Oop === 0 : false;
        },
        positions() {
            return this.vuexState.data ? this.vuexState.data.Positions : [];
        },
        variants() {
            return this.vuexState.data ? this.vuexState.data.Variant : [];
        },
        position_sizes() {
            return this.vuexState.data ? this.vuexState.data.Sizes : [];
        },
        formSwitches() {
            return [
                {
                    vModel: 'slotStatus',
                    label: 'Slot Status',
                },
                {
                    vModel: 'lazyLoad',
                    label: "Lazy Loading",
                },
                {
                    vModel: 'splitQueue',
                    label: "Ist Teil der Split Queue",
                    1: '',
                    0: '',
                },
                {
                    vModel: 'adLabel',
                    label: 'Hat Anzeigenkennzeichnung ?',
                    1: '',
                    0: '',
                }
            ];
        },
        validate() {
            return this.name_errors.length === 0 &&
                this.variant_errors.length === 0 &&
                this.posVar_errors.length === 0;
        },
        name_errors() {
            return Utils.handleError(this, 'name', 'Position ist erforderlich.');
        },
        variant_errors() {
            return Utils.handleError(this, 'variant', 'Variante ist erforderlich.');
        },
        posVar_errors() {
            return Utils.handleError(this, 'posVar', 'Pos-Variable ist erforderlich.');
        },
    },
    methods: {
        init() {
            if (this.position) {
                //edit position
                this.name = this.position.PositionName;
                this.variant = this.variants.find(x => x.Id === this.position.Variant)
                this.slotStatus = this.position.Status;
                this.posVar = this.position.Position
                this.lazyLoad = this.position.LazyLoad;
                this.splitQueue = this.position.SplitQueue;
                this.adLabel = this.position.Adlabel;
                this.position2 = this.position.Position2 === -1 ? '' : this.position.Position2;
                this.note = this.position.Note || '';
                this.sizes = [];
                this.position.Sizes.forEach((size) => {
                    let position_size = this.position_sizes.find(x => x.Id === size.ConfigSizeId);
                    if (position_size) this.sizes.push(position_size);
                })
            } else {
                //create position
                this.name = this.positions[0];
                this.variant = this.variants[0]
                this.sizes = [];
                this.slotStatus = 1;
                this.lazyLoad = 0;
                this.splitQueue = 0;
                this.adLabel = 1;
                this.posVar = 1;
                this.note = null;
            }
        },
        fetchPositions() {
            let json = {};
            if (this.selectedSite && this.selectedType) {
                json.publisher_id = this.selectedSite.PublisherId;
                json.site_id = this.selectedSite.SiteId;
                json.type_id = this.selectedType.SitetypeId;
            }
            this.$store.dispatch('zones/fetchData', {
                publisher_id: this.selectedSite.PublisherId,
                site_id: null,
                type_id: null,
                zone_id: null
            })
        },
        async submit() {
            this.$v.$touch();
            if (this.validate) {
                this.overwrite ? this.overwriteLoading = true : this.loading = true;
                let data;
                try {
                    if (this.position) {
                        this.currentCancelToken = 'editPosition';
                        data = await this.$store.dispatch('zones/editPosition', this.getBody());
                    } else {
                        this.currentCancelToken = 'createPosition';
                        data = await this.$store.dispatch('zones/createPosition', this.getBody());
                    }
                    if (data.Status) {
                        this.overwrite ? this.overwriteLoading = false : this.loading = false;
                        if (this.overwrite) this.showOverwriteDialog = true;
                        this.currentCancelToken = null;
                        this.overwrite = false;
                        this.successSnackbar = true;
                        this.cancel();
                    }
                } catch (e) {
                    this.overwrite ? this.overwriteLoading = false : this.loading = false;
                }
            }
            this.loading = false;
        },
        submitAndOverwrite() {
            this.overwrite = true;
            this.submit();
        },
        getBody() {
            let json = {
                site_id: this.selectedSite.SiteId,
                publisher_id: this.selectedSite.PublisherId,
                type_id: this.selectedType.SitetypeId,
                zone_id: this.vuexState.zoneDetails.Zone.ZoneId,
                PositionNameId: this.name.ConfigPositionnameId,
                Variant: this.variant.Id,
                Status: this.slotStatus,
                LazyLoad: this.lazyLoad,
                splitQueue: this.splitQueue,
                AdLabel: this.adLabel,
                Position: this.posVar,
                Sizes: this.getSizes(),
                Note: this.note === '' ? null : this.note,
            }
            if (this.position) {
                json.position_id = this.position.PositionId;
                json.Position2 = this.position.Position2 === -1 ? '' : this.position.Position2;
            }
            return json;
        },
        getSizes() {
            let ret = []
            this.sizes.forEach((size) => {
                ret.push(size.Id);
            })
            return ret;
        },
        removeSize(item) {
            const index = this.sizes.findIndex(x => x.Id === item.Id);
            if (index >= 0) this.sizes.splice(index, 1);
        },
        addMultipleSizes() {
            let multiSizeArr = this.multipleSizes.split(';').map(x => x.trim());
            multiSizeArr.forEach((size) => {
                if (!this.sizes.map(x => x.Name).includes(size)) {
                    if (this.position_sizes.find(x => x.Name === size)) {
                        this.sizes.push(this.position_sizes.find(x => x.Name === size));
                    }
                }
            })
            this.$root.$emit('snackbar', 'Sizes erfolgreich hinzugefügt', 'success')
            this.showAddMultipleSizesDialog = false;
            this.multipleSizes = null;
        },
        cancel() {
            if (this.currentCancelToken && (this.loading || this.overwriteLoading)) {
                this.$store.commit('zones/cancelRequest', this.currentCancelToken);
                if (this.loading) this.loading = false;
                if (this.overwriteLoading) this.overwriteLoading = false;
            }
            if (this.showDeletePositionDialog) this.showDeletePositionDialog = false;
            this.init();
            this.$emit('input', false)
        },
    },
    watch: {
        positions() {
            if (this.positions.length !== 0 && this.position === null) {
                this.name = this.positions[0];
            }
        },
        variants() {
            if (this.variants.length !== 0 && this.position === null) {
                this.variant = this.variants[0];
            }
        },
        position() {
            this.init();
        },
        value() {
            if (this.value) this.init();
        }
    }
}
</script>

<style scoped>

</style>